import type { ActionPayload, GameState } from '../shared/types.js';
import { getSocket } from './socket.js';

let lastSeq = -1;
let latest: GameState | null = null;
let onState: (state: GameState) => void = () => {};
let raf = 0;
let queued: GameState | null = null;

/** How long a click may own the dice / buttons before we give them back. */
const ACK_TIMEOUT_MS = 8000;

/**
 * Actions waiting on the server, each holding the callback that releases
 * whatever the click put on screen.
 *
 * A second click is a duplicate when a request for that action is already
 * outstanding, which we know for certain, rather than when it lands inside
 * some window of milliseconds, which is a guess. The old guess dropped the
 * click silently and left the dice spinning with nothing to stop them.
 */
const inFlight = new Map<string, { release: () => void; timer: number }>();

let watchingSocket = false;

/**
 * Two builds on different houses are two different requests, so the target is
 * part of the request's identity. Only a repeat of the very same request is a
 * duplicate.
 */
function keyOf(payload: ActionPayload): string {
  return payload.propertyPosition === undefined
    ? payload.action
    : `${payload.action}:${payload.propertyPosition}`;
}

/** Release a request, whether the server answered it or the socket died. */
function settle(key: string): void {
  const pending = inFlight.get(key);
  if (!pending) return;
  inFlight.delete(key);
  window.clearTimeout(pending.timer);
  pending.release();
}

function settleAll(): void {
  for (const key of Array.from(inFlight.keys())) settle(key);
}

/**
 * Losing the connection is the answer we get when the server never sends one,
 * so it settles every outstanding request instead of leaving the board
 * mid-click until something else happens to repaint it.
 */
function watchSocket(): void {
  if (watchingSocket) return;
  watchingSocket = true;
  getSocket().on('disconnect', () => {
    settleAll();
    repaint();
  });
}

/**
 * Clicking a button disables it until the next render. If the server rejects
 * the action, nothing else repaints the panel, so the player is left on their
 * own turn with dead buttons — which reads as a frozen game. Repainting the
 * last known state re-enables them without inventing any state of our own.
 */
function repaint(): void {
  if (latest) onState(latest);
}

export function setStateHandler(handler: (state: GameState) => void): void {
  onState = handler;
}

export function getLatestState(): GameState | null {
  return latest;
}

/** True when this packet was newer than what is already on screen. */
export function applyState(state: GameState, immediate = false): boolean {
  if (typeof state.seq === 'number') {
    if (state.seq <= lastSeq) return false;
    lastSeq = state.seq;
    // The server moved. Dice and buttons are no longer owned by the click.
    settleAll();
  }
  latest = state;
  if (immediate) {
    queued = null;
    onState(state);
    return true;
  }
  queued = state;
  if (!raf) {
    raf = requestAnimationFrame(() => {
      raf = 0;
      const next = queued;
      queued = null;
      if (next) onState(next);
    });
  }
  return true;
}

function isGameState(value: unknown): value is GameState {
  if (!value || typeof value !== 'object') return false;
  const rec = value as Record<string, unknown>;
  if ('error' in rec) return false;
  return typeof rec.phase === 'string' && Array.isArray(rec.players);
}

function unwrapAck(value: unknown): unknown {
  if (Array.isArray(value) && value.length > 0) return value[0];
  return value;
}

/**
 * Fire an action and paint from the ack so the click never waits on NPCs.
 *
 * Returns whether the action was actually sent. `onSettled` runs once the
 * request is resolved either way, so anything the click started on screen is
 * tied to the request that started it and cannot outlive it.
 */
export function sendAction(payload: ActionPayload, onSettled: () => void = () => {}): boolean {
  const key = keyOf(payload);
  if (inFlight.has(key)) return false;
  watchSocket();

  const timer = window.setTimeout(() => {
    if (!inFlight.has(key)) return;
    settle(key);
    repaint();
  }, ACK_TIMEOUT_MS);
  inFlight.set(key, { release: onSettled, timer });

  const t0 = performance.now();
  getSocket().emit('game:action', payload, (response) => {
    const ms = Math.round(performance.now() - t0);
    if (ms > 200) console.warn('[monopoly] slow ack', payload.action, ms, 'ms');
    try {
      const state = unwrapAck(response);
      // A duplicate seq still has to give the buttons back. Dropping the
      // ack and clearing the timeout left the clicked control disabled.
      if (!isGameState(state) || !applyState(state, true)) repaint();
    } catch (err) {
      console.error('[monopoly] action paint failed', err);
      try { repaint(); } catch { /* paint is already broken */ }
    } finally {
      settle(key);
    }
  });

  return true;
}
